import React from "react";
import { toast } from "sonner";
import { MessageCircle, Mail, LinkIcon } from "lucide-react";

const TYPE_LABEL = { level_1: "Term Completion", level_2: "Age-Band Graduate" };

function bandLabel(id) {
  if (!id) return "";
  return "Ages " + id.replace("age_", "").replace("-", "\u2013");
}

function buildMessage(cert, verifyUrl) {
  const name = cert.child_first_name || cert.child_name || "Our learner";
  const label = TYPE_LABEL[cert.cert_type] || cert.cert_type;
  const band = bandLabel(cert.age_band);
  const suffix = cert.cert_type === "level_2"
    ? (cert.grade ? ` with ${cert.grade}` : "")
    : (cert.term ? ` (Term ${cert.term})` : "");
  return `${name} just earned their FutureTec Global ${label} certificate for ${band}${suffix}! Verify: ${verifyUrl}`;
}

/**
 * Share row for a single certificate.
 * Props:
 *   cert:        { cert_id_slug, cert_type, age_band, term, grade, child_first_name?, child_name? }
 *   verifyUrl:   absolute URL
 *   variant:     'compact' | 'wide' (default 'compact')
 *   testIdPrefix: string
 */
export default function ShareRow({ cert, verifyUrl, variant = "compact", testIdPrefix = "share" }) {
  const message = buildMessage(cert, verifyUrl);
  const encoded = encodeURIComponent(message);
  const waHref = `https://wa.me/?text=${encoded}`;
  const mailtoHref = `mailto:?subject=${encodeURIComponent("A FutureTec Global certificate to verify")}&body=${encoded}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      toast.success("Link copied!");
    } catch {
      // Fallback: temporary textarea
      const ta = document.createElement("textarea");
      ta.value = verifyUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Link copied!"); }
      catch { toast.error("Could not copy the link."); }
      ta.remove();
    }
  };

  return (
    <div className={`ftg-share ftg-share--${variant}`} data-testid={`${testIdPrefix}-row`}>
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        className="ftg-btn ftg-btn--ghost ftg-btn--sm"
        data-testid={`${testIdPrefix}-whatsapp`}
      >
        <MessageCircle size={13} /> WhatsApp
      </a>
      <a
        href={mailtoHref}
        className="ftg-btn ftg-btn--ghost ftg-btn--sm"
        data-testid={`${testIdPrefix}-email`}
      >
        <Mail size={13} /> Email
      </a>
      <button
        type="button"
        onClick={copyLink}
        className="ftg-btn ftg-btn--ghost ftg-btn--sm"
        data-testid={`${testIdPrefix}-copy`}
      >
        <LinkIcon size={13} /> Copy link
      </button>
    </div>
  );
}
